import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Dimensions,
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import CloseIcon from '../../assets/icons/close-icon.svg';
import { REDESIGN_COLORS } from '../styles/colors';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';

const { width: screenWidth } = Dimensions.get('window');
const isMobileWidth = screenWidth < 768;

/**
 * ModelPickerInline — Shared component for selecting up to N car models.
 * Used in both the onboarding question flow (light theme) and /pick-models (dark theme).
 *
 * Data: Pass `makesModels` for pre-fetched data, or `region` to let the component fetch its own.
 */
const ModelPickerInline = ({
  selectedModels = [],
  onAddModel,
  onRemoveModel,
  maxModels = 3,
  theme = 'dark',
  scrollRef,
  makesModels: externalMakesModels,
  region,
}) => {
  const t = theme === 'light' ? lightTheme : darkTheme;

  // Internal data fetching (when no external data provided)
  const [internalMakesModels, setInternalMakesModels] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(!externalMakesModels);

  const makesModelsData = externalMakesModels || internalMakesModels;

  useEffect(() => {
    if (externalMakesModels) {
      setIsLoadingData(false);
      return;
    }
    const fetchData = async () => {
      try {
        const { data, error } = await supabase
          .from('car_makes_models')
          .select('make_name, model_name')
          .eq('region', region || 'UK')
          .order('make_name')
          .order('model_name');

        if (error) throw error;
        setInternalMakesModels(data || []);
      } catch (err) {
        logger.error('[ModelPickerInline] Error fetching makes/models:', err);
      } finally {
        setIsLoadingData(false);
      }
    };
    fetchData();
  }, [region, externalMakesModels]);

  // Picker state
  const [showPicker, setShowPicker] = useState(selectedModels.length === 0);
  const [selectedMake, setSelectedMake] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [makeSearch, setMakeSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [showMakeDropdown, setShowMakeDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Pulse animation
  const pulseAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (selectedModels.length < maxModels) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, { toValue: 1.02, duration: 1200, useNativeDriver: true }),
          Animated.timing(pulseAnim, { toValue: 1, duration: 1200, useNativeDriver: true }),
        ])
      );
      pulse.start();
      return () => pulse.stop();
    }
  }, [selectedModels.length, maxModels, pulseAnim]);

  // Derived data
  const makes = useMemo(() => {
    const uniqueMakes = [...new Set(makesModelsData.map(item => item.make_name))];
    return uniqueMakes.sort();
  }, [makesModelsData]);

  const models = useMemo(() => {
    if (!selectedMake) return [];
    return makesModelsData
      .filter(item => item.make_name === selectedMake)
      .map(item => item.model_name)
      .sort();
  }, [makesModelsData, selectedMake]);

  const filteredMakes = useMemo(() => {
    if (!makeSearch) return makes;
    const lower = makeSearch.toLowerCase();
    return makes.filter(m => m.toLowerCase().includes(lower));
  }, [makes, makeSearch]);

  const filteredModels = useMemo(() => {
    if (!modelSearch) return models;
    const lower = modelSearch.toLowerCase();
    return models.filter(m => m.toLowerCase().includes(lower));
  }, [models, modelSearch]);

  const openPicker = useCallback(() => {
    setShowPicker(true);
    setSelectedMake(null);
    setSelectedModel(null);
    setMakeSearch('');
    setModelSearch('');
    setSubmitError(null);
  }, []);

  const closePicker = useCallback(() => {
    setShowPicker(false);
    setSelectedMake(null);
    setSelectedModel(null);
    setMakeSearch('');
    setModelSearch('');
    setShowMakeDropdown(false);
    setShowModelDropdown(false);
  }, []);

  const prevLengthRef = useRef(selectedModels.length);
  useEffect(() => {
    const prev = prevLengthRef.current;
    const curr = selectedModels.length;
    if (curr < prev && curr < maxModels) {
      openPicker();
    }
    prevLengthRef.current = curr;
  }, [selectedModels.length, maxModels, openPicker]);

  const handleAddModel = useCallback(() => {
    if (!selectedMake || !selectedModel) return;

    const isDuplicate = selectedModels.some(
      m => m.make === selectedMake && m.model === selectedModel
    );
    if (isDuplicate) {
      setSubmitError('This car is already in your list.');
      return;
    }

    onAddModel(selectedMake, selectedModel);
    if (selectedModels.length + 1 < maxModels) {
      openPicker();
    } else {
      closePicker();
    }
  }, [selectedMake, selectedModel, selectedModels, maxModels, onAddModel, openPicker, closePicker]);

  if (isLoadingData) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={t.submitBg} />
      </View>
    );
  }

  return (
    <View>
      {/* Selected model cards */}
      {selectedModels.map((model, index) => (
        <View key={`${model.make}-${model.model}`} style={[styles.selectedCard, { backgroundColor: t.selectedCardBg, borderColor: t.selectedCardBorder }]}>
          <View style={[styles.selectedCardGlow, { backgroundColor: t.selectedCardGlow }]} />
          <View style={styles.selectedCardContent}>
            <View style={styles.selectedCardText}>
              <Text style={[styles.selectedCardMake, { color: t.selectedCardMakeText }]}>{model.make}</Text>
              <Text style={[styles.selectedCardModel, { color: t.selectedCardModelText }]}>{model.model.toUpperCase()}</Text>
            </View>
            <TouchableOpacity
              onPress={() => onRemoveModel(index)}
              style={styles.removeButton}
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            >
              <Icon name="close-circle" size={22} color={t.removeIcon} />
            </TouchableOpacity>
          </View>
        </View>
      ))}

      {/* Add car slot */}
      {selectedModels.length < maxModels && !showPicker && (
        <Animated.View style={{ transform: [{ scale: pulseAnim }] }}>
          <Pressable style={[styles.addSlot, { borderColor: t.addSlotBorder }]} onPress={openPicker}>
            <View style={[styles.addSlotIconCircle, { backgroundColor: t.addSlotIconBg }]}>
              <Icon name="plus" size={24} color={t.addSlotIcon} />
            </View>
            <Text style={[styles.addSlotText, { color: t.addSlotText }]}>
              {selectedModels.length === 0 ? 'Add your first car' : 'Add another car'}
            </Text>
          </Pressable>
        </Animated.View>
      )}

      {/* Inline picker */}
      {showPicker && (
        <View style={[styles.pickerCard, {
          backgroundColor: t.pickerCardBg,
          borderColor: t.pickerCardBorder,
          ...Platform.select({
            web: { boxShadow: `0px 4px 24px ${t.pickerCardShadow}` },
            default: { elevation: 8 },
          }),
        }]}>
          {/* Close button */}
          <TouchableOpacity style={[styles.pickerClose, { backgroundColor: t.closeBg }]} onPress={closePicker}>
            <CloseIcon width={12} height={12} />
          </TouchableOpacity>

          <Text style={[styles.pickerTitle, { color: t.titleText }]}>Search for a car</Text>
          <Text style={[styles.pickerSubtitle, { color: t.subtitleText }]}>
            Choose a make and model to add to your tracked cars.
          </Text>

          {/* Make dropdown */}
          <View style={styles.dropdownWrapper}>
            <Text style={[styles.dropdownLabel, { color: t.labelText }]}>Make</Text>
            <TouchableOpacity
              style={[styles.dropdownTrigger, { backgroundColor: t.dropdownTriggerBg, borderColor: t.dropdownTriggerBorder }]}
              onPress={() => {
                const opening = !showMakeDropdown;
                setShowMakeDropdown(opening);
                setShowModelDropdown(false);
                if (opening) setTimeout(() => scrollRef?.current?.scrollToEnd?.({ animated: true }), 100);
              }}
            >
              <Text style={selectedMake ? [styles.dropdownValue, { color: t.dropdownValueText }] : [styles.dropdownPlaceholder, { color: t.dropdownPlaceholderText }]}>
                {selectedMake || 'Select make...'}
              </Text>
              <Text style={[styles.dropdownArrow, { color: t.dropdownPlaceholderText }]}>{showMakeDropdown ? '\u25B2' : '\u25BC'}</Text>
            </TouchableOpacity>
            {showMakeDropdown && (
              <View style={[styles.dropdownList, {
                backgroundColor: t.dropdownListBg,
                borderColor: t.dropdownListBorder,
                ...Platform.select({
                  web: { boxShadow: `0px 4px 16px ${t.dropdownListShadow}` },
                  default: { elevation: 10 },
                }),
              }]}>
                <TextInput
                  style={[styles.dropdownSearch, { color: t.dropdownSearchText, borderBottomColor: t.dropdownSearchBorder }]}
                  placeholder="Type to filter..."
                  placeholderTextColor={t.dropdownPlaceholderText}
                  value={makeSearch}
                  onChangeText={setMakeSearch}
                />
                <FlatList
                  data={filteredMakes}
                  keyExtractor={item => item}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      style={[styles.dropdownItem, item === selectedMake && { backgroundColor: t.dropdownItemSelectedBg }]}
                      onPress={() => {
                        setSelectedMake(item);
                        setSelectedModel(null);
                        setShowMakeDropdown(false);
                        setMakeSearch('');
                      }}
                    >
                      <Text style={[
                        styles.dropdownItemText,
                        { color: t.dropdownItemText },
                        item === selectedMake && { fontFamily: 'Satoshi-Medium', color: t.dropdownItemSelectedText },
                      ]}>{item}</Text>
                    </TouchableOpacity>
                  )}
                  style={styles.dropdownFlatList}
                  keyboardShouldPersistTaps="handled"
                />
              </View>
            )}
          </View>

          {/* Model dropdown */}
          <View style={styles.dropdownWrapper}>
            <Text style={[styles.dropdownLabel, { color: t.labelText }]}>Model</Text>
            <TouchableOpacity
              style={[
                styles.dropdownTrigger,
                { backgroundColor: t.dropdownTriggerBg, borderColor: t.dropdownTriggerBorder },
                !selectedMake && styles.dropdownDisabled,
              ]}
              onPress={() => {
                if (!selectedMake) return;
                const opening = !showModelDropdown;
                setShowModelDropdown(opening);
                setShowMakeDropdown(false);
                if (opening) setTimeout(() => scrollRef?.current?.scrollToEnd?.({ animated: true }), 100);
              }}
              disabled={!selectedMake}
            >
              <Text style={selectedModel ? [styles.dropdownValue, { color: t.dropdownValueText }] : [styles.dropdownPlaceholder, { color: t.dropdownPlaceholderText }]}>
                {selectedModel || 'Select model...'}
              </Text>
              <Text style={[styles.dropdownArrow, { color: t.dropdownPlaceholderText }]}>{showModelDropdown ? '\u25B2' : '\u25BC'}</Text>
            </TouchableOpacity>
            {showModelDropdown && (
              <View style={[styles.dropdownList, {
                backgroundColor: t.dropdownListBg,
                borderColor: t.dropdownListBorder,
                ...Platform.select({
                  web: { boxShadow: `0px 4px 16px ${t.dropdownListShadow}` },
                  default: { elevation: 10 },
                }),
              }]}>
                <TextInput
                  style={[styles.dropdownSearch, { color: t.dropdownSearchText, borderBottomColor: t.dropdownSearchBorder }]}
                  placeholder="Type to filter..."
                  placeholderTextColor={t.dropdownPlaceholderText}
                  value={modelSearch}
                  onChangeText={setModelSearch}
                />
                <FlatList
                  data={filteredModels}
                  keyExtractor={item => item}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      style={[styles.dropdownItem, item === selectedModel && { backgroundColor: t.dropdownItemSelectedBg }]}
                      onPress={() => {
                        setSelectedModel(item);
                        setShowModelDropdown(false);
                        setModelSearch('');
                      }}
                    >
                      <Text style={[
                        styles.dropdownItemText,
                        { color: t.dropdownItemText },
                        item === selectedModel && { fontFamily: 'Satoshi-Medium', color: t.dropdownItemSelectedText },
                      ]}>{item}</Text>
                    </TouchableOpacity>
                  )}
                  style={styles.dropdownFlatList}
                  keyboardShouldPersistTaps="handled"
                />
              </View>
            )}
          </View>

          {/* Add Car button */}
          <Pressable
            style={[styles.pickerSubmit, { backgroundColor: t.submitBg }, (!selectedMake || !selectedModel) && { opacity: 0.5 }]}
            onPress={handleAddModel}
            disabled={!selectedMake || !selectedModel}
          >
            <Text style={styles.pickerSubmitText}>Add Car</Text>
          </Pressable>

          {submitError && (
            <Text style={styles.errorText}>{submitError}</Text>
          )}
        </View>
      )}

      {/* Hint text when no cars selected */}
      {selectedModels.length === 0 && !showPicker && (
        <View style={styles.hintCard}>
          <Text style={[styles.hintText, { color: t.subtitleText }]}>
            {'Select at least one car to start\nscanning the market for you.'}
          </Text>
        </View>
      )}
    </View>
  );
};

// --- Theme color maps ---
const darkTheme = {
  pickerCardBg: '#13192A',
  pickerCardBorder: 'rgba(255,255,255,0.08)',
  pickerCardShadow: 'rgba(0,0,0,0.4)',
  titleText: REDESIGN_COLORS.textPrimary,
  subtitleText: 'rgba(255,255,255,0.55)',
  labelText: 'rgba(255,255,255,0.7)',
  dropdownTriggerBg: 'rgba(255,255,255,0.06)',
  dropdownTriggerBorder: 'rgba(255,255,255,0.15)',
  dropdownValueText: REDESIGN_COLORS.textPrimary,
  dropdownPlaceholderText: 'rgba(255,255,255,0.35)',
  dropdownListBg: '#1D242F',
  dropdownListBorder: 'rgba(255,255,255,0.1)',
  dropdownListShadow: 'rgba(0,0,0,0.5)',
  dropdownSearchText: REDESIGN_COLORS.textPrimary,
  dropdownSearchBorder: 'rgba(255,255,255,0.08)',
  dropdownItemText: 'rgba(255,255,255,0.85)',
  dropdownItemSelectedBg: 'rgba(5, 86, 247, 0.15)',
  dropdownItemSelectedText: '#5B8DEF',
  selectedCardBg: REDESIGN_COLORS.cardBg,
  selectedCardBorder: REDESIGN_COLORS.cardSelectedBorder,
  selectedCardGlow: 'rgba(255,255,255,0.08)',
  selectedCardMakeText: REDESIGN_COLORS.textPrimary,
  selectedCardModelText: REDESIGN_COLORS.textPrimary,
  removeIcon: 'rgba(255,255,255,0.4)',
  addSlotBorder: 'rgba(255,255,255,0.15)',
  addSlotIconBg: 'rgba(255,255,255,0.08)',
  addSlotIcon: 'rgba(255,255,255,0.6)',
  addSlotText: 'rgba(255,255,255,0.5)',
  submitBg: '#0556F7',
  closeBg: 'rgba(255,255,255,0.1)',
};

const lightTheme = {
  pickerCardBg: '#FFFFFF',
  pickerCardBorder: '#E5E7EB',
  pickerCardShadow: 'rgba(0,0,0,0.06)',
  titleText: '#0E0E0E',
  subtitleText: '#6B7280',
  labelText: '#374151',
  dropdownTriggerBg: '#FFFFFF',
  dropdownTriggerBorder: '#E1E7F2',
  dropdownValueText: '#0E0E0E',
  dropdownPlaceholderText: '#9CA3AF',
  dropdownListBg: '#FFFFFF',
  dropdownListBorder: '#E5E7EB',
  dropdownListShadow: 'rgba(0,0,0,0.08)',
  dropdownSearchText: '#0E0E0E',
  dropdownSearchBorder: '#E5E7EB',
  dropdownItemText: '#374151',
  dropdownItemSelectedBg: 'rgba(0, 0, 0, 0.04)',
  dropdownItemSelectedText: '#0E0E0E',
  selectedCardBg: '#FFFFFF',
  selectedCardBorder: '#E5E7EB',
  selectedCardGlow: 'rgba(0, 0, 0, 0.02)',
  selectedCardMakeText: '#0E0E0E',
  selectedCardModelText: '#374151',
  removeIcon: '#9CA3AF',
  addSlotBorder: '#D1D5DB',
  addSlotIconBg: 'rgba(0,0,0,0.04)',
  addSlotIcon: '#6B7280',
  addSlotText: '#6B7280',
  submitBg: '#0E0E0E',
  closeBg: 'rgba(0,0,0,0.06)',
};

// --- Structural styles (theme-independent) ---
const styles = StyleSheet.create({
  loadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Selected car cards
  selectedCard: {
    height: 80,
    borderRadius: 16,
    marginBottom: 12,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  selectedCardGlow: {
    position: 'absolute',
    right: -20,
    top: -20,
    width: 120,
    height: 120,
    borderRadius: 60,
    ...Platform.select({
      web: { filter: 'blur(40px)' },
      default: {},
    }),
  },
  selectedCardContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
  },
  selectedCardText: {
    gap: 4,
  },
  selectedCardMake: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 20,
    lineHeight: 20 * 1.4,
  },
  selectedCardModel: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 12,
    letterSpacing: 1.92,
  },
  removeButton: {
    padding: 8,
  },

  // Add car slot
  addSlot: {
    height: 80,
    backgroundColor: 'transparent',
    borderRadius: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderStyle: 'dashed',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  addSlotIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addSlotText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 16,
  },

  // Picker card
  pickerCard: {
    borderRadius: 24,
    borderWidth: 1,
    padding: isMobileWidth ? 24 : 40,
    paddingTop: 48,
    marginBottom: 16,
    position: 'relative',
  },
  pickerClose: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  pickerTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: isMobileWidth ? 20 : 24,
    lineHeight: isMobileWidth ? 28 : 32,
    marginBottom: 8,
  },
  pickerSubtitle: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    lineHeight: 22,
    marginBottom: 24,
  },

  // Dropdowns
  dropdownWrapper: {
    marginBottom: 16,
  },
  dropdownLabel: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 6,
  },
  dropdownTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
  },
  dropdownDisabled: {
    opacity: 0.6,
  },
  dropdownValue: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 16,
    lineHeight: 24,
  },
  dropdownPlaceholder: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 16,
    lineHeight: 24,
  },
  dropdownArrow: {
    fontSize: 10,
  },
  dropdownList: {
    borderRadius: 12,
    marginTop: 4,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  dropdownSearch: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    lineHeight: 20,
    borderBottomWidth: 1,
    ...Platform.select({
      web: { outlineStyle: 'none' },
      default: {},
    }),
  },
  dropdownFlatList: {
    maxHeight: 300,
  },
  dropdownItem: {
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  dropdownItemText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 15,
    lineHeight: 22,
  },

  // Picker submit
  pickerSubmit: {
    borderRadius: 32,
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 8,
  },
  pickerSubmitText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 16,
    color: '#FFFFFF',
  },

  // Hint
  hintCard: {
    height: isMobileWidth ? 80 : undefined,
    paddingVertical: isMobileWidth ? 0 : 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hintText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 16,
    lineHeight: 16 * 1.4,
    textAlign: 'center',
  },

  // Error
  errorText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: '#FF4444',
    textAlign: 'center',
    marginTop: 12,
  },
});

export default ModelPickerInline;
